import { useState, useEffect, useRef, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  X,
  CheckCheck,
  Clock,
  Megaphone,
  ChevronRight,
  Loader2,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { notificationsApi, announcementsApi, Notification, Announcement } from '@/api/endpoints';
import { formatDistanceToNow } from 'date-fns';
import { useNotificationSSE, SSENotification } from '@/hooks/useNotificationSSE';
import { getCategoryConfig } from '@/config/notificationConfig';

interface NotificationBellProps {
  className?: string;
}

export const NotificationBell = ({ className = '' }: NotificationBellProps) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'notifications' | 'announcements'>('notifications');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadAnnouncements, setUnreadAnnouncements] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const totalUnread = unreadNotifications + unreadAnnouncements;

  // SSE real-time notifications
  const { connected, error: sseError } = useNotificationSSE({
    onNotification: (sseNotif: SSENotification) => {
      // Prepend the new notification to the list
      const newNotif: Notification = {
        id: sseNotif.id,
        tenant_id: '',
        user_id: '',
        type: 'in_app',
        title: sseNotif.title,
        message: sseNotif.message,
        category: sseNotif.category,
        action_url: sseNotif.action_url,
        icon: sseNotif.icon,
        data: sseNotif.data,
        status: 'sent',
        created_at: sseNotif.created_at,
      };
      setNotifications((prev) => [newNotif, ...prev.slice(0, 9)]);
      setUnreadNotifications((prev) => prev + 1);
    },
  });

  // Fetch data on mount and periodically (only poll when SSE is disconnected)
  useEffect(() => {
    fetchData();
    // Only poll if SSE is not connected
    if (!connected) {
      const interval = setInterval(fetchData, 30000);
      return () => clearInterval(interval);
    }
  }, [connected]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchData = async () => {
    try {
      const [notifResponse, announcementResponse] = await Promise.all([
        notificationsApi.list({ limit: 10, unread_only: false }).catch(() => ({ data: { notifications: [], unread_count: 0 } })),
        announcementsApi.list({ limit: 10 }).catch(() => ({ data: { announcements: [], unread_count: 0 } })),
      ]);

      setNotifications(notifResponse.data?.notifications || []);
      setUnreadNotifications(notifResponse.data?.unread_count || 0);
      setAnnouncements(announcementResponse.data?.announcements || []);
      setUnreadAnnouncements(announcementResponse.data?.unread_count || 0);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await notificationsApi.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, status: 'read' as const } : n))
      );
      setUnreadNotifications((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      setLoading(true);
      await notificationsApi.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, status: 'read' as const })));
      setUnreadNotifications(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAnnouncementAsRead = async (id: string) => {
    try {
      await announcementsApi.markAsRead(id);
      setUnreadAnnouncements((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark announcement as read:', error);
    }
  };

  const handleDeleteNotification = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await notificationsApi.delete(id);
      const notification = notifications.find((n) => n.id === id);
      setNotifications((prev) => prev.filter((n) => n.id !== id));
      if (notification?.status !== 'read') {
        setUnreadNotifications((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (notification.status !== 'read') {
      handleMarkAsRead(notification.id);
    }
    if (notification.action_url) {
      setIsOpen(false);
      navigate(notification.action_url);
    }
  };

  const getNotificationIcon = (notification: Notification) => {
    const config = getCategoryConfig(notification.category);
    return createElement(config.icon, { className: `w-4 h-4 ${config.colorClass}` });
  };

  const getNotificationBg = (notification: Notification) => {
    const config = getCategoryConfig(notification.category);
    return config.bgClass;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'border-l-red-500';
      case 'high':
        return 'border-l-amber-500';
      case 'low':
        return 'border-l-gray-500';
      default:
        return 'border-l-teal-500';
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg text-white/60 hover:text-white/90 hover:bg-white/5 transition-colors"
      >
        <Bell className="w-5 h-5" />
        {totalUnread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center bg-gradient-to-r from-teal-500 to-cyan-500 text-white text-[10px] font-bold rounded-full px-1">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
        {/* SSE connection indicator */}
        {connected && (
          <span className="absolute bottom-0.5 right-0.5 w-2 h-2 rounded-full bg-green-500" title="Real-time connected" />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-[#12121a] border border-white/[0.08] rounded-xl shadow-2xl overflow-hidden z-50">
          {/* Header */}
          <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <h3 className="text-white font-semibold">Notifications</h3>
              {sseError && (
                <span className="text-[10px] text-amber-400" title={sseError}>polling</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadNotifications > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={loading}
                  className="text-xs text-teal-400 hover:text-teal-300 transition-colors flex items-center gap-1"
                >
                  {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCheck className="w-3 h-3" />}
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-lg text-gray-400 hover:text-white hover:bg-white/[0.08] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/[0.06]">
            <button
              onClick={() => setActiveTab('notifications')}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors relative ${
                activeTab === 'notifications'
                  ? 'text-teal-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <Bell className="w-4 h-4" />
                Notifications
                {unreadNotifications > 0 && (
                  <span className="bg-teal-500/20 text-teal-400 text-[10px] px-1.5 py-0.5 rounded-full">
                    {unreadNotifications}
                  </span>
                )}
              </span>
              {activeTab === 'notifications' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('announcements')}
              className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors relative ${
                activeTab === 'announcements'
                  ? 'text-teal-400'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <Megaphone className="w-4 h-4" />
                Announcements
                {unreadAnnouncements > 0 && (
                  <span className="bg-purple-500/20 text-purple-400 text-[10px] px-1.5 py-0.5 rounded-full">
                    {unreadAnnouncements}
                  </span>
                )}
              </span>
              {activeTab === 'announcements' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-500" />
              )}
            </button>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto custom-scrollbar">
            {activeTab === 'notifications' ? (
              notifications.length === 0 ? (
                <div className="py-12 text-center">
                  <div className="w-12 h-12 rounded-xl bg-white/[0.04] flex items-center justify-center mx-auto mb-3">
                    <Bell className="w-6 h-6 text-gray-500" />
                  </div>
                  <p className="text-gray-400 text-sm">No notifications</p>
                </div>
              ) : (
                <div className="divide-y divide-white/[0.04]">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`px-4 py-3 hover:bg-white/[0.02] transition-colors cursor-pointer group ${
                        notification.status !== 'read' ? 'bg-teal-500/[0.03]' : ''
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-lg ${getNotificationBg(notification)} flex items-center justify-center flex-shrink-0`}>
                          {getNotificationIcon(notification)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className={`text-sm font-medium ${notification.status !== 'read' ? 'text-white' : 'text-gray-300'}`}>
                              {notification.title}
                            </p>
                            <button
                              onClick={(e) => handleDeleteNotification(notification.id, e)}
                              className="p-1 rounded opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 hover:bg-red-500/10 transition-all"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{notification.message}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            {notification.category && (
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.04] text-gray-400">
                                {getCategoryConfig(notification.category).label}
                              </span>
                            )}
                            <Clock className="w-3 h-3 text-gray-500" />
                            <span className="text-[10px] text-gray-500">
                              {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                            </span>
                            {notification.status !== 'read' && (
                              <span className="w-1.5 h-1.5 rounded-full bg-teal-500 ml-auto" />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : announcements.length === 0 ? (
              <div className="py-12 text-center">
                <div className="w-12 h-12 rounded-xl bg-white/[0.04] flex items-center justify-center mx-auto mb-3">
                  <Megaphone className="w-6 h-6 text-gray-500" />
                </div>
                <p className="text-gray-400 text-sm">No announcements</p>
              </div>
            ) : (
              <div className="divide-y divide-white/[0.04]">
                {announcements.map((announcement) => (
                  <div
                    key={announcement.id}
                    onClick={() => handleMarkAnnouncementAsRead(announcement.id)}
                    className={`px-4 py-3 hover:bg-white/[0.02] transition-colors cursor-pointer border-l-2 ${getPriorityColor(announcement.priority)}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center flex-shrink-0">
                        {announcement.is_pinned ? (
                          <AlertTriangle className="w-4 h-4 text-amber-400" />
                        ) : (
                          <Megaphone className="w-4 h-4 text-purple-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-white">{announcement.title}</p>
                          {announcement.is_pinned && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                              Pinned
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{announcement.content}</p>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Clock className="w-3 h-3 text-gray-500" />
                          <span className="text-[10px] text-gray-500">
                            {formatDistanceToNow(new Date(announcement.created_at), { addSuffix: true })}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 border-t border-white/[0.06]">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/notifications');
              }}
              className="w-full text-center text-sm text-teal-400 hover:text-teal-300 transition-colors flex items-center justify-center gap-1"
            >
              View all notifications
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
